import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Check } from 'lucide-react';

const SimulatedBsCalculator = ({ onComplete }) => {
  const [mode, setMode] = useState('multiply'); // multiply | divide
  const [input, setInput] = useState('10');
  const [copied, setCopied] = useState(false);
  const vesPerUsdt = 464.74;

  const parsed = parseFloat(input || '0');
  const isValid = Number.isFinite(parsed) && parsed >= 0;
  const result = isValid
    ? mode === 'divide'
      ? parsed / vesPerUsdt
      : parsed * vesPerUsdt
    : 0;

  const handleCopy = async () => {
    if (!isValid || result <= 0) return;
    const toCopy = Number(result).toFixed(2);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(toCopy);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // ignore
    }
  };

  return (
    <div className="tutorial-simulated-wallet">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.3 }}
        className="tutorial-wallet-card"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <span role="img" aria-label="calculator">🧮</span>
            Calculadora rápida Bs
          </h3>
          <button
            onClick={onComplete}
            className="text-white/50 hover:text-white transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-[11px] text-text/60">
            Convierte usando la tasa BCV actual.
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setMode('multiply'); setInput('10'); setCopied(false); }}
              className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-colors ${
                mode === 'multiply'
                  ? 'bg-accent/20 text-accent'
                  : 'bg-glass text-text/70 hover:bg-glass/70'
              }`}
            >
              Multiplicar
            </button>
            <button
              type="button"
              onClick={() => { setMode('divide'); setInput('1000'); setCopied(false); }}
              className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-colors ${
                mode === 'divide'
                  ? 'bg-accent/20 text-accent'
                  : 'bg-glass text-text/70 hover:bg-glass/70'
              }`}
            >
              Dividir
            </button>
          </div>

          <div className="space-y-1">
            <div className="text-text/60 text-xs">
              {mode === 'divide' ? 'Monto base (ej. Bs)' : 'Monto base (ej. USD)'}
            </div>
            <input
              type="number"
              min="0"
              step="0.01"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (copied) setCopied(false);
              }}
              className="input-glass w-full"
              placeholder={mode === 'divide' ? 'Ej: 1000' : 'Ej: 10'}
            />
          </div>

          <div className="mt-2 p-3 rounded-lg bg-glass flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <div className="text-[11px] text-text/60">Resultado</div>
              <button
                type="button"
                onClick={handleCopy}
                disabled={!isValid || result <= 0}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                  copied
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'bg-white/10 hover:bg-white/15 text-text/90 hover:text-white border border-white/10'
                } disabled:opacity-30 disabled:pointer-events-none`}
                title="Copiar monto"
              >
                {copied ? (
                  <>
                    <Check size={12} className="text-emerald-400" />
                    <span>¡Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy size={12} />
                    <span>Copiar</span>
                  </>
                )}
              </button>
            </div>
            <div className="text-sm font-semibold text-text/90">
              {isValid
                ? result.toLocaleString('es-VE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) + (mode === 'divide' ? ' USD' : ' Bs')
                : mode === 'divide'
                ? '0,00 USD'
                : '0,00 Bs'}
            </div>
            <div className="text-[10px] text-text/50">
              Tasa BCV: {vesPerUsdt.toLocaleString('es-VE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} Bs
            </div>
          </div>

          <div className="rounded-xl border border-fire-orange/20 bg-fire-orange/5 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text/50">Fuegos</div>
                <div className="text-sm font-semibold text-fire-orange">🔥 {(parsed * 300).toFixed(2)}</div>
              </div>
              <button
                type="button"
                className="badge-fire cursor-pointer hover:scale-105 transition-transform"
                onClick={onComplete}
              >
                <span className="text-sm">🔥</span>
                <span className="text-xs font-semibold">Abrir</span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className="w-full rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/15 transition-colors"
                onClick={onComplete}
              >
                Comprar fuegos
              </button>
              <button
                type="button"
                className="w-full rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-3 py-2 text-[11px] font-semibold text-cyan-300 hover:bg-cyan-500/15 transition-colors"
                onClick={onComplete}
              >
                Historial
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SimulatedBsCalculator;
